import { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import API from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Icon } from '../components/SvgIcons';
import './SuperAdminFinalizedPapers.css';

const PAGE_SIZE = 12;

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date)) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Finalized Papers tab for tenant admins.
 *  - university_admin: papers finalized by COE.
 *  - college_admin / hod: college-level papers finalized by the moderator.
 * The backend returns the appropriate `level` based on the caller's role.
 */
export default function TenantFinalizedPapers() {
  const { showToast } = useToast();
  const { user } = useContext(AuthContext);
  const isCollege = user?.role === 'college_admin' || user?.role === 'hod';
  const [level, setLevel] = useState(isCollege ? 'college' : 'university');
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [viewing, setViewing] = useState(null);
  const [sets, setSets] = useState([]);
  const [setsLoading, setSetsLoading] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState({});
  const [showEncryptionModal, setShowEncryptionModal] = useState(false);
  const [encryptionPassword, setEncryptionPassword] = useState('');
  const [pendingDownloadData, setPendingDownloadData] = useState(null);
  const [showDownloadProgressModal, setShowDownloadProgressModal] = useState(false);

  const finalizerLabel = level === 'university' ? 'COE' : 'Moderator';
  const ownerLabel = level === 'university' ? 'University' : 'College';

  const fetchPapers = useCallback(async () => {
    setLoading(true);
    try {
      if (isCollege) {
        // College admin / HOD: finalized papers by all moderators in the college.
        const { data } = await API.get('/papers/moderator/finalized-papers');
        setLevel('college');
        setPapers((data.papers || []).map(p => ({
          id: p.paper_id,
          level: 'college',
          title: p.paper_title,
          subject_name: p.subject_name || null,
          owner: p.examiner_name || '—',
          finalized_by_name: null,
          total_marks: p.total_marks || 0,
          sets_count: p.categorized_count || 0,
          finalized_at: p.updated_at || p.created_at,
        })));
      } else {
        const { data } = await API.get('/finalized-papers/list');
        setLevel(data.level || 'university');
        setPapers(data.papers || []);
      }
      setPage(1);
    } catch {
      showToast('Failed to load finalized papers', 'error');
      setPapers([]);
    } finally {
      setLoading(false);
    }
  }, [showToast, isCollege]);

  useEffect(() => { fetchPapers(); }, [fetchPapers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return papers;
    return papers.filter(p =>
      (p.title || '').toLowerCase().includes(q) ||
      (p.owner || '').toLowerCase().includes(q) ||
      (p.finalized_by_name || '').toLowerCase().includes(q) ||
      (p.subject_name || '').toLowerCase().includes(q)
    );
  }, [papers, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const openSets = async (paper) => {
    setViewing(paper); setSets([]); setSetsLoading(true);
    try {
      if (isCollege) {
        const { data } = await API.get(`/moderator-categorization/paper/${paper.id}/categorized-sets`);
        const catSets = data.categorized_papers || [];
        // Fetch each set's questions for inline preview.
        const detailed = await Promise.all(catSets.map(async (s) => {
          const setId = s.paper_id; // categorized-sets exposes set_id as paper_id
          let questions = [];
          try {
            const { data: d } = await API.get(`/moderator-categorization/set/${setId}/details`);
            questions = (d.questions || []).map((q, i) => {
              const main = q.main_question_number;
              const sub = q.sub_question_number;
              const number = main ? (sub ? `Q${main}.${sub}` : `Q${main}`) : `Q${i + 1}`;
              let options = q.options;
              if (options && typeof options === 'string') { try { options = JSON.parse(options); } catch { options = null; } }
              return {
                number, question_text: q.question_text, question_type: q.question_type,
                marks: q.marks, difficulty: q.difficulty,
                options: Array.isArray(options) ? options : null,
              };
            });
          } catch { /* leave empty */ }
          return {
            id: setId,
            set_label: s.paper_category || `set${s.set_number}`,
            title: `Set ${s.set_number}${s.paper_category ? ` · ${s.paper_category}` : ''}`,
            total_marks: questions.reduce((sum, q) => sum + (Number(q.marks) || 0), 0),
            questions,
          };
        }));
        setSets(detailed);
      } else {
        const { data } = await API.get(`/finalized-papers/${paper.level}/${paper.id}/sets`);
        setSets(data.sets || []);
      }
    } catch {
      showToast('Failed to load sets', 'error');
      setSets([]);
    } finally {
      setSetsLoading(false);
    }
  };

  const closeModal = () => { setViewing(null); setSets([]); };

  const handleDownloadPDF = async (paperId, paperTitle, customContent = null, password = null) => {
    try {
      setDownloadingPDF(prev => ({
        ...prev, [paperId]: true
      }));

      // Always use AI-enhanced PDF generation with custom content
      let response;
      if (customContent) {
        console.log('🤖 Generating AI-enhanced PDF with custom content:', {
          header: customContent.header ? 'Yes' : 'No',
          instructions: customContent.instructions ? 'Yes' : 'No',
          footer: customContent.footer ? 'Yes' : 'No',
          questions: customContent.questions?.length || 0,
          encrypted: password ? 'Yes' : 'No'
        });

        showToast('🤖 AI is formatting your paper... This may take a few seconds.', 'info');

        response = await API.post(`/papers/moderator/paper/${paperId}/download-pdf-ai`, {
          header: customContent.header,
          instructions: customContent.instructions,
          footer: customContent.footer,
          questions: customContent.questions,
          password: password || undefined
        }, {
          responseType: 'blob',
          timeout: 60000 // 60 second timeout for AI processing
        });
      } else {
        // Fallback to regular PDF if no custom content
        response = await API.get(`/papers/moderator/paper/${paperId}/download-pdf`, {
          responseType: 'blob',
          params: password ? { password } : {}
        });
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = password
        ? `${paperTitle.replace(/[^a-z0-9]/gi, '_')}_AI_Encrypted.pdf`
        : `${paperTitle.replace(/[^a-z0-9]/gi, '_')}_AI.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      if (password) {
        showToast('✅ Encrypted AI-formatted PDF downloaded successfully!', 'success');
      } else {
        showToast('✅ AI-formatted PDF downloaded successfully!', 'success');
      }

      // Close modals
      setShowEncryptionModal(false);
      setShowDownloadProgressModal(false);
    } catch (error) {
      console.error('Download PDF error:', error);
      showToast(error.response?.data?.message || 'Failed to download PDF', 'error');
    } finally {
      setDownloadingPDF(prev => ({
        ...prev, [paperId]: false
      }));
      setShowDownloadProgressModal(false);
    }
  };

  const handleDownloadClick = (paperId, paperTitle, customContent) => {
    // Store the download data and show encryption modal
    setPendingDownloadData({ paperId, paperTitle, customContent });
    setShowEncryptionModal(true);
    setEncryptionPassword('');
  };

  const confirmDownloadWithEncryption = async () => {
    if (pendingDownloadData) {
      // Validate password if provided
      if (encryptionPassword && encryptionPassword.trim().length > 0 && encryptionPassword.trim().length < 6) {
        showToast('Password must be at least 6 characters long', 'error');
        return;
      }

      const { paperId, paperTitle, customContent } = pendingDownloadData;

      // Close encryption modal and show download progress modal
      setShowEncryptionModal(false);
      setShowDownloadProgressModal(true);

      // Start download
      await handleDownloadPDF(paperId, paperTitle, customContent, encryptionPassword || null);

      // Close download progress modal after completion
      setShowDownloadProgressModal(false);
      
      // Reset state
      setPendingDownloadData(null);
      setEncryptionPassword('');
    }
  };

  const downloadSets = async (set = null) => {
    if (!viewing) return;

    // Use AI-enhanced download for all roles
    if (set) {
      // Build custom content from set questions for AI-enhanced PDF
      const customContent = {
        header: '',
        instructions: '',
        footer: '',
        questions: set.questions || []
      };
      // Pass set.id for individual set downloads (this is the set_id in database)
      handleDownloadClick(set.id, `${viewing.title}_${set.set_label || 'set'}`, customContent);
    } else {
      // Download all sets - need to download each set individually
      if (sets.length === 0) {
        showToast('No questions available to download', 'error');
        return;
      }

      // For downloading all sets, we'll need a different approach
      // For now, show a message to download individually
      showToast('Please download sets individually by clicking each set\'s download button', 'info');
    }
  };

  return (
    <div className="sfp-page">
      <div className="sfp-header">
        <h1>Finalized Papers</h1>
        <p>
          {level === 'university'
            ? 'Question papers finalized by the Controller of Examinations (COE).'
            : 'College-level question papers finalized by the moderator.'}
        </p>
      </div>

      <div className="sfp-toolbar">
        <div />
        <input
          className="sfp-search"
          type="text"
          placeholder="Search title, subject or finalized by…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      <div className="sfp-card">
        {loading ? (
          <div className="sfp-spin-wrap"><div className="sfp-spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="sfp-empty">
            <div className="sfp-empty-icon"><Icon name="FileText" size={42} /></div>
            <h3 style={{ margin: '0 0 0.3rem' }}>No finalized papers found</h3>
            <p style={{ margin: 0 }}>
              {search ? 'Try a different search term.' : `Papers finalized by the ${finalizerLabel.toLowerCase()} will appear here.`}
            </p>
          </div>
        ) : (
          <>
            <div className="sfp-table-wrap">
              <table className="sfp-table">
                <thead>
                  <tr>
                    <th>Paper Title</th>
                    <th>Subject</th>
                    <th>{ownerLabel}</th>
                    <th>Finalized By ({finalizerLabel})</th>
                    <th>Marks</th>
                    <th>Sets</th>
                    <th>Finalized</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map(p => (
                    <tr key={`${p.level}-${p.id}`}>
                      <td className="sfp-title-cell">{p.title}</td>
                      <td className="sfp-muted">{p.subject_name || '—'}</td>
                      <td>{p.owner || '—'}</td>
                      <td>{p.finalized_by_name || '—'}</td>
                      <td>{p.total_marks}</td>
                      <td><span className="sfp-sets-pill">{p.sets_count}</span></td>
                      <td className="sfp-muted">{fmtDate(p.finalized_at)}</td>
                      <td>
                        <button
                          className="sfp-view-btn"
                          onClick={() => openSets(p)}
                          disabled={!p.sets_count}
                          title={p.sets_count ? 'View the sets in this paper' : 'No sets available'}
                        >
                          <Icon name="Eye" size={14} /> View Sets
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="sfp-pagination">
                <span className="sfp-pg-info">
                  Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
                <div className="sfp-pg-btns">
                  <button className="sfp-pg-btn" disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>Previous</button>
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{safePage} / {totalPages}</span>
                  <button className="sfp-pg-btn" disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {viewing && (
        <div className="sfp-overlay" onClick={closeModal}>
          <div className="sfp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sfp-modal-head">
              <div>
                <h3>Sets · {viewing.title}</h3>
                <p>
                  {viewing.finalized_by_name ? `Finalized by ${viewing.finalized_by_name} · ` : ''}{viewing.owner}
                </p>
              </div>
              <div className="sfp-modal-actions">
                <button
                  className="sfp-dl-btn"
                  onClick={() => downloadSets()}
                  disabled={downloadingPDF[viewing.id] || setsLoading || sets.length === 0}
                  title={sets.length === 0 ? 'No sets to download' : 'Download all sets as AI-enhanced PDF'}
                >
                  <Icon name="Download" size={15} /> {downloadingPDF[viewing.id] ? '⏳ AI Formatting...' : '📥 AI Enhanced PDF'}
                </button>
                <button className="sfp-modal-close" onClick={closeModal}>{'×'}</button>
              </div>
            </div>
            <div className="sfp-modal-body">
              {setsLoading ? (
                <div className="sfp-spin-wrap"><div className="sfp-spinner" /></div>
              ) : sets.length === 0 ? (
                <div className="sfp-q-empty">No sets found for this paper.</div>
              ) : (
                sets.map((set, si) => (
                  <div className="sfp-set" key={si}>
                    <div className="sfp-set-head">
                      <span className="sfp-set-name">
                        <Icon name="Clipboard" size={15} /> {set.title || set.set_label || `Set ${si + 1}`}
                      </span>
                      <div className="sfp-set-head-right">
                        <span className="sfp-set-meta">
                          {set.questions?.length || 0} question{(set.questions?.length || 0) === 1 ? '' : 's'} · {set.total_marks} marks
                        </span>
                        <button
                          className="sfp-set-dl"
                          onClick={() => downloadSets(set)}
                          disabled={downloadingPDF[set?.id] || downloadingPDF[viewing.id]}
                          title="Download this set as AI-enhanced PDF"
                        >
                          <Icon name="Download" size={13} /> {(downloadingPDF[set?.id] || downloadingPDF[viewing.id]) ? '⏳ AI Formatting...' : '📥 AI PDF'}
                        </button>
                      </div>
                    </div>
                    {(!set.questions || set.questions.length === 0) ? (
                      <div className="sfp-q-empty">No questions in this set.</div>
                    ) : (
                      set.questions.map((q, qi) => (
                        <div className="sfp-q" key={qi}>
                          <div className="sfp-q-top">
                            <span className="sfp-q-num">{q.number || `Q${qi + 1}`}{q.question_type ? ` · ${q.question_type}` : ''}</span>
                            {q.marks != null && <span className="sfp-q-marks">{q.marks} marks{q.difficulty ? ` · ${q.difficulty}` : ''}</span>}
                          </div>
                          <div className="sfp-q-text">{q.question_text || '—'}</div>
                        </div>
                      ))
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Encryption Modal */}
      {showEncryptionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }} onClick={() => setShowEncryptionModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 1rem 0' }}>🔐 Optional Encryption</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Add a password to encrypt the PDF (minimum 6 characters, or leave blank for no encryption):
            </p>
            <input
              type="password"
              placeholder="Enter password (optional)"
              value={encryptionPassword}
              onChange={(e) => setEncryptionPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--border-color)',
                borderRadius: '0.25rem',
                marginBottom: '1rem',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEncryptionModal(false)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid var(--border-color)',
                  background: 'transparent',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDownloadWithEncryption}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Progress Modal */}
      {showDownloadProgressModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.5rem',
            padding: '2rem',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid var(--primary)',
                borderTop: '3px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }} />
            </div>
            <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600' }}>🤖 AI is formatting your paper...</p>
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>This may take a few seconds</p>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
}
